import type { ViewForSiderFieldsFragment, ViewGroupFieldsFragment } from '@salescore/client-api'
import type { DataNode as AntdNode } from 'antd/es/tree'
import type { i18n } from 'i18next'
import type { IconType } from 'rc-tree/lib/interface'
import type { ReactElement } from 'react'

import type { ExpandedAntdNodeSerializable } from './convertFlatNodesToAntdTree'

export type ExpandedAntdNode = Omit<AntdNode, 'children'> & {
  searchKey: string
  children: ExpandedAntdNode[]
  item: ViewForSiderFieldsFragment | ViewGroupFieldsFragment
  title: React.ReactNode | ((data: AntdNode) => React.ReactNode)
  icon: IconType
  recursiveNodeIds: Set<string>
}

export function serializeExpandedAntdNode(nodes: ExpandedAntdNodeSerializable[]): ExpandedAntdNodeSerializable[] {
  return nodes.map((node) => ({
    ...node,
    title: undefined,
    icon: undefined,
    children: serializeExpandedAntdNode(node.children),
  }))
}

const flatten = (tree: ExpandedAntdNodeSerializable): ExpandedAntdNodeSerializable[] => [
  tree,
  ...tree.children.flatMap((child) => [child, ...flatten(child)]),
]

export function deserializeExpandedAntdNode(
  serializables: ExpandedAntdNodeSerializable[],
  t: i18n['t'],
): ExpandedAntdNode[] {
  return serializables.map((node) => ({
    ...node,
    title: getTitle(node.item, t),
    icon: getIcon(node.item.emoji),
    children: deserializeExpandedAntdNode(node.children, t),
    recursiveNodeIds: new Set(flatten(node).map((node) => node.item.id)),
  }))
}

// ホバー時の詳細情報 (タイトル/作成日時/作成者/最終更新日時・更新者) は Ant Design の Popover/Tooltip ではなく HTML の title 属性で表示する。
// Popover はスクロール・リサイズのたびに表示位置を再計算するため、サイドバースクロール中にちらつき・煩わしさが出る。それを避けるための意図的な選択 (commit 8d4225ea52, 2024-03)。
// ネイティブの title 属性は表示まで遅延が入りスクロールに追従しないため、ここに Popover/Tooltip を持ち込むとちらつきが再発する。踏襲すること。
function getTitle(view: ViewForSiderFieldsFragment | ViewGroupFieldsFragment, t: i18n['t']) {
  const title = `${t(`タイトル`)}: ${view.name}
  ${t(`作成日時`)}: ${t(`{{datetime, datetimestr}}`, { datetime: view.createdAt })}
  ${t(`作成者`)}: ${view.createdBy?.name ?? '-'}
  ${t(`最終更新日時`)}: ${t(`{{datetime, datetimestr}}`, {
    datetime: view.__typename === 'ViewForSider' ? view.intentionallyUpdatedAt : view.updatedAt,
  })}
  ${t(`最終更新者`)}: ${view.updatedBy?.name ?? '-'}`

  return (
    <span title={title}>
      {view.name}
      {view.createdBy?.name !== undefined && <span className="ml-1 text-xs opacity-50">{view.createdBy.name}</span>}
    </span>
  )
}

function getIcon(emoji: string | null | undefined): ReactElement | undefined {
  if (emoji === null || emoji === undefined) {
    return undefined
  }

  return (
    <span
      style={{
        paddingRight: 5,
      }}
    >
      {emoji}
    </span>
  )
}
